import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";

export const textParam = (defaultValue = "") => ({
    defaultValue,
    parse: value => value,
    serialize: value => String(value),
});

export const choiceParam = (defaultValue, choices) => ({
    ...textParam(defaultValue),
    parse: value => choices.includes(value) ? value : defaultValue,
});

export const pageParam = {
    defaultValue: 1,
    parse: value => /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) > 0
        ? Number(value) : 1,
    serialize: String,
};

export const arrayParam = {
    defaultValue: [],
    parse: value => value.split(",").filter(Boolean),
    serialize: value => value.join(","),
};

export const dateParam = {
    defaultValue: null,
    parse: value => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
        const [year, month, day] = value.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
            ? date : null;
    },
    serialize: value => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
    },
};

// The URL is the source of truth, including when the browser restores history.
// Keep schemas outside components so decoded objects retain stable references.
export default function useListSearchParams(schema) {
    const [searchParams, setSearchParams] = useSearchParams();
    const state = useMemo(() => Object.fromEntries(
        Object.entries(schema).map(([key, field]) => [
            key,
            searchParams.has(key) ? field.parse(searchParams.get(key)) : field.defaultValue,
        ])
    ), [schema, searchParams]);

    const update = useCallback(patch => {
        const changes = typeof patch === "function" ? patch(state) : patch;
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            const field = schema[key];
            const serialized = field.serialize(value);
            if (serialized === field.serialize(field.defaultValue)) next.delete(key);
            else next.set(key, serialized);
        });
        if (next.toString() !== searchParams.toString()) setSearchParams(next);
    }, [schema, searchParams, setSearchParams, state]);

    return [state, update];
}
